/**
 * Analytics "Split by" preference — ORDERED multi-level splits. Pure, storage-injected
 * persistence (no React), namespaced per base + block installation exactly like dockViews
 * (two bases sharing an origin can never read each other's splits).
 *
 * Storage is VERSIONED:
 *   · v1 (legacy): the bare lens id string ("model") — one single-level split;
 *   · v2: {"v":2,"splits":["model","phase"]} — the ordered layer stack.
 * loadSplits migrates v1 transparently (an existing single-split pref becomes the one-layer
 * stack — nothing is lost). Load is defensive: unknown lens ids, the CURRENT primary lens, and
 * duplicates all drop; depth caps at MAX_SPLITS. Dropping a layer PROMOTES deeper layers up
 * (array filter keeps order). buildOrder IS allowed as a split layer — its buildOrderBucket is a
 * stable global-quintile partition (Earliest→Latest + No build order); it's only barred as the
 * PRIMARY colour lens (that view is the heat-ramp sequence grid, handled in AnalyticsPanel).
 */
import { LENSES, type LensId } from './lenses';
import type { StorageLike } from '../panels/dock/dockViews';

/** Max split depth. State, persistence, and the picker all read this — 3 levels is a one-line change. */
export const MAX_SPLITS = 3;

/** lfe.<baseId>[.<blockId>].analytics.split */
export const splitPrefKey = (baseId: string, blockId?: string): string =>
  `lfe.${baseId || 'base'}${blockId ? '.' + blockId : ''}.analytics.split`;

/** The ONE rules gate for a split stack: keeps only known lenses that aren't the primary, deduped
 *  in order, capped at `max`. (buildOrder is allowed as a split — only barred as the primary lens.)
 *  Removing an entry promotes deeper layers up. */
export function sanitizeSplits(raw: unknown, primary: LensId, max = MAX_SPLITS): LensId[] {
  if (!Array.isArray(raw)) return [];
  const out: LensId[] = [];
  for (const v of raw) {
    if (typeof v !== 'string' || !(v in LENSES) || v === primary) continue;
    if (out.includes(v as LensId)) continue;
    out.push(v as LensId);
    if (out.length >= max) break;
  }
  return out;
}

/** Checkbox semantics for the picker (pure):
 *   · unchecked → APPEND as the deepest layer;
 *   · unchecked while already at `max` → REPLACE the deepest layer;
 *   · checked → REMOVE the layer; deeper layers PROMOTE up one level (filter keeps order). */
export function toggleSplit(splits: LensId[], id: LensId, max = MAX_SPLITS): LensId[] {
  if (splits.includes(id)) return splits.filter(s => s !== id);
  if (splits.length >= max) return [...splits.slice(0, max - 1), id];
  return [...splits, id];
}

export function loadSplits(store: StorageLike | null, key: string, primary: LensId): LensId[] {
  try {
    const v = store && store.getItem(key);
    if (!v) return [];
    const head = v.trimStart()[0];
    if (head === '{' || head === '[') {
      const parsed = JSON.parse(v);
      const arr = Array.isArray(parsed) ? parsed : (parsed && parsed.v === 2 ? parsed.splits : []);
      return sanitizeSplits(arr, primary);
    }
    return sanitizeSplits([v], primary); // v1 legacy: bare lens id → one-layer stack
  } catch { return []; }
}

export function saveSplits(store: StorageLike | null, key: string, splits: LensId[]): void {
  try {
    if (!store) return;
    if (splits.length) store.setItem(key, JSON.stringify({ v: 2, splits }));
    else store.removeItem(key);
  } catch { /* private mode */ }
}

/** Browser localStorage when reachable, null in sandboxed/denied contexts (callers degrade). */
export const safeStore = (): StorageLike | null => {
  try { return window.localStorage; } catch { return null; }
};
